"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  bytesToBase64,
  bytesToHex,
  bytesToText,
  byteToBits,
  byteToHex,
  crackSingleByte,
  parseBase64Bytes,
  parseHexBytes,
  textToBytes,
  xorBytes,
} from "@/lib/tool-kit/ciphers-misc/xor";

type KeyFormat = "text" | "hex";
type DataFormat = "hex" | "base64";

const ROWS_SHOWN = 16;
const CANDIDATES_SHOWN = 5;
const ASCII_FIRST = 33;
const ASCII_LAST = 126;
const SPACE_CODE = 32;
const SPACE_MARK = "␣";
const UNPRINTABLE_MARK = "·";

/** English strings. A translated content file overrides these through `ui`. */
export const XOR_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherTextHex: "Cipher text (hex bytes)",
  cipherTextBase64: "Cipher text (Base64)",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "Attack at dawn",
  sampleKey: "key",
  key: "Key",
  keyHint:
    "The key is repeated along the message. As text it is read as UTF-8 bytes; as hex it is bytes such as 2a 5f.",
  keyFormat: "Key is written as",
  keyFormatText: "Text",
  keyFormatHex: "Hex bytes",
  dataFormat: "Cipher text is shown as",
  dataFormatHex: "Hex",
  dataFormatBase64: "Base64",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  bytesCount: "one: {count} byte|other: {count} bytes",
  noKey: "Enter a key. With an empty key nothing is changed.",
  badKey: "The key is not whole hex bytes (pairs of 0–9 and a–f).",
  badHex: "The cipher text is not valid hex. Use pairs of 0–9 and a–f.",
  badBase64: "The cipher text is not valid Base64.",
  notText:
    "The result is not valid UTF-8 text, so some characters show as �. The key or the format is probably not the one used to encode.",
  working: "Bit by bit",
  workingIntro:
    "XOR compares two bits and gives 1 when they differ and 0 when they match: 0 ⊕ 0 = 0, 0 ⊕ 1 = 1, 1 ⊕ 0 = 1, 1 ⊕ 1 = 0. Each byte of the input is combined with the next byte of the key, which repeats.",
  rowsShown:
    "one: Showing the first byte.|other: Showing the first {count} bytes.",
  colNumber: "Byte",
  colInput: "Input (hex)",
  colKey: "Key (hex)",
  colResult: "XOR result (hex)",
  colChar: "Character",
  cell: "{bits} ({hex})",
  emptyWorking: "Type a message to see the bits.",
  crackToggle: "Try every single-byte key",
  crackHeading: "One-byte keys that give readable text",
  crackHint:
    "All 256 values of a one-byte key are tried; only results that are valid text without control characters are listed, the most English-like first. Use this when the key is a single byte.",
  crackKey: "Key {hex}",
  crackUse: "Use this key",
  crackNone: "No one-byte key turns this into readable text.",
} as const;

type Strings = typeof XOR_CIPHER_STRINGS;

interface XorRun {
  /** The input as bytes, or null when it could not be read. */
  data: Uint8Array | null;
  /** The key as bytes, or null when it could not be read. */
  key: Uint8Array | null;
  result: Uint8Array;
  output: string;
  valid: boolean;
  error: "badKey" | "badHex" | "badBase64" | null;
}

function readData(
  input: string,
  mode: CipherMode,
  format: DataFormat
): { data: Uint8Array | null; error: XorRun["error"] } {
  if (mode === "encode") {
    return { data: textToBytes(input), error: null };
  }
  const data =
    format === "hex" ? parseHexBytes(input) : parseBase64Bytes(input);
  if (data === null) {
    return { data, error: format === "hex" ? "badHex" : "badBase64" };
  }
  return { data, error: null };
}

function runXor(
  input: string,
  keyText: string,
  mode: CipherMode,
  keyFormat: KeyFormat,
  format: DataFormat
): XorRun {
  const key =
    keyFormat === "hex" ? parseHexBytes(keyText) : textToBytes(keyText);
  const { data, error } = readData(input, mode, format);
  const empty = new Uint8Array();
  if (key === null || data === null) {
    return {
      data,
      key,
      result: empty,
      output: "",
      valid: true,
      error: key === null ? "badKey" : error,
    };
  }
  const result = xorBytes(data, key);
  if (mode === "encode") {
    const output =
      format === "hex" ? bytesToHex(result) : bytesToBase64(result);
    return { data, key, result, output, valid: true, error: null };
  }
  const { text, valid } = bytesToText(result);
  return { data, key, result, output: text, valid, error: null };
}

function characterOf(byte: number): string {
  if (byte === SPACE_CODE) {
    return SPACE_MARK;
  }
  return byte >= ASCII_FIRST && byte <= ASCII_LAST
    ? String.fromCharCode(byte)
    : UNPRINTABLE_MARK;
}

function describeByte(byte: number, strings: Strings): string {
  return t(strings.cell, { bits: byteToBits(byte), hex: byteToHex(byte) });
}

function workingRows(
  run: XorRun,
  strings: Strings,
  fmt: (value: number) => string
): string[][] {
  const { data, key, result } = run;
  if (data === null || key === null || key.length === 0) {
    return [];
  }
  return [...result.slice(0, ROWS_SHOWN)].map((byte, index) => [
    fmt(index + 1),
    describeByte(data[index], strings),
    describeByte(key[index % key.length], strings),
    describeByte(byte, strings),
    characterOf(byte),
  ]);
}

export default function XorCipherWidget() {
  const strings = useToolUi(XOR_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyText, setKeyText] = useState<string>(strings.sampleKey);
  const [keyFormat, setKeyFormat] = useState<KeyFormat>("text");
  const [format, setFormat] = useState<DataFormat>("hex");
  const [crack, setCrack] = useState(false);

  const run = runXor(input, keyText, mode, keyFormat, format);
  const errorText = run.error === null ? null : strings[run.error];
  const rows = workingRows(run, strings, fmt);
  const cipherLabel =
    format === "hex" ? strings.cipherTextHex : strings.cipherTextBase64;
  const byteTotal = run.result.length;
  const candidates =
    mode === "decode" && crack && run.data !== null
      ? crackSingleByte(run.data).slice(0, CANDIDATES_SHOWN)
      : [];

  function adoptKey(byte: number) {
    setKeyFormat("hex");
    setKeyText(byteToHex(byte));
  }

  const keyControls = (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          hint={strings.keyHint}
          id="xr-key"
          label={strings.key}
          mono
          onChange={setKeyText}
          value={keyText}
        />
        <SelectField
          id="xr-key-format"
          label={strings.keyFormat}
          onChange={setKeyFormat}
          options={[
            { value: "text", label: strings.keyFormatText },
            { value: "hex", label: strings.keyFormatHex },
          ]}
          value={keyFormat}
        />
        <SelectField
          id="xr-format"
          label={strings.dataFormat}
          onChange={setFormat}
          options={[
            { value: "hex", label: strings.dataFormatHex },
            { value: "base64", label: strings.dataFormatBase64 },
          ]}
          value={format}
        />
      </div>
      {mode === "decode" ? (
        <Checkbox
          checked={crack}
          label={strings.crackToggle}
          onChange={setCrack}
        />
      ) : null}
    </div>
  );

  const notices = (
    <div className="space-y-2">
      {errorText ? <ErrorNotice>{errorText}</ErrorNotice> : null}
      {run.key?.length === 0 ? <Notice>{strings.noKey}</Notice> : null}
      {run.valid ? null : <Notice>{strings.notText}</Notice>}
    </div>
  );

  const extras =
    crack && mode === "decode" && run.data !== null ? (
      <section className={`${CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.crackHeading}</h2>
        <p className={HINT}>{strings.crackHint}</p>
        {candidates.length === 0 ? (
          <Notice>{strings.crackNone}</Notice>
        ) : (
          <ul className="divide-y divide-border">
            {candidates.map((candidate) => (
              <li
                className="flex flex-wrap items-center gap-3 py-2"
                key={candidate.key}
              >
                <span className="w-24 shrink-0 font-mono text-muted-foreground text-xs">
                  {t(strings.crackKey, { hex: byteToHex(candidate.key) })}
                </span>
                <span className="min-w-0 flex-1 break-all font-mono text-foreground text-sm">
                  {candidate.text}
                </span>
                <button
                  className={BUTTON_SMALL}
                  onClick={() => adoptKey(candidate.key)}
                  type="button"
                >
                  {strings.crackUse}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    ) : null;

  const working =
    rows.length > 0 ? (
      <div className="space-y-3">
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.workingIntro}
        </p>
        <DataTable
          head={[
            strings.colNumber,
            strings.colInput,
            strings.colKey,
            strings.colResult,
            strings.colChar,
          ]}
          rows={rows}
        />
        {run.result.length > ROWS_SHOWN ? (
          <p className={HINT}>
            {tPlural(strings.rowsShown, locale, ROWS_SHOWN, {
              count: fmt(ROWS_SHOWN),
            })}
          </p>
        ) : null}
      </div>
    ) : (
      <Notice>{strings.emptyWorking}</Notice>
    );

  return (
    <CipherWorkbench
      extras={extras}
      idPrefix="xr"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : cipherLabel,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? cipherLabel : strings.decodedText,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={notices}
      onInputChange={setInput}
      onModeChange={setMode}
      output={run.output}
      outputNotes={
        run.error === null && byteTotal > 0 ? (
          <p className={HINT}>
            {tPlural(strings.bytesCount, locale, byteTotal, {
              count: fmt(byteTotal),
            })}
          </p>
        ) : null
      }
      working={working}
    />
  );
}
